'use client';

import { useContext, useState } from 'react';
import type { AffiliateCardParamsBase, ProductCardPromotion } from '@/app_components/AffiliateProductCard/types';
import { Icon } from '@/app_components/common/Icon';
import { GtmContext } from '@/app_lib/providers/GtmProvider';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

const iconMap = {
  failed: 'CircleX',
  isCopied: 'CircleCheck',
} as const;

const messageDuration = 1000;

export const Promotion = ({
  promotion,
  affiliateCardParamsBase,
}: {
  promotion: ProductCardPromotion;
  affiliateCardParamsBase: AffiliateCardParamsBase;
}) => {
  const { pageData } = useContext(GtmContext);
  const [messageState, setMessageState] = useState<'failed' | 'isCopied'>('isCopied');
  const [toolTip, setToolTip] = useState<boolean>(false);

  const onCopy = () => {
    // Without the label added, promotion just functions as a coupon
    if (toolTip || promotion.label) {
      return;
    }

    navigator.clipboard
      .writeText(promotion.code)
      .then(() => {
        setToolTip(true);
        setMessageState('isCopied');
        trackEvent({
          ...pageData,
          affiliateCardParams: {
            ...affiliateCardParamsBase,
            clickTarget: 'Coupon',
          },
          eventAction: 'affiliate_card_click',
        });
        setTimeout(() => setToolTip(false), messageDuration);
      })
      .catch(() => {
        setToolTip(true);
        setMessageState('failed');
        setTimeout(() => setToolTip(false), messageDuration);
      });
  };

  let icon: 'Zap' | 'CircleX' | 'CircleCheck' | 'Copy' = 'Copy';
  if (promotion.label) {
    icon = 'Zap';
  } else if (toolTip) {
    icon = iconMap[messageState];
  }

  return (
    <>
      <button
        className={cn(
          'flex gap-sm typography-xs success-text font-graphik-compact font-normal text-left',
          !promotion.label && 'hover:cursor-pointer',
        )}
        data-cy={ContentPlacement.AffiliateProductCardPromotion}
        onClick={onCopy}
        type='button'
      >
        <Icon
          className='success-text'
          icon={icon}
          size='sm'
        />
        {promotion.label ? (
          <p>{promotion.label}</p>
        ) : (
          <p>
            Use code <span className='font-semibold'>{promotion.code}</span> to get {promotion.discount} off your
            purchase
          </p>
        )}
      </button>
      <div
        className={cn(
          'absolute flex flex-row gap-sm py-xxxs px-xxs success-surface success-text top-[-33px] font-medium text-xs left-1/2 -translate-x-1/2',
          'transition-all duration-200 ease-out',
          toolTip ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none',
        )}
      >
        <Icon
          icon={iconMap[messageState]}
          size='sm'
        />
        <p className='font-graphik-compact typography-xs font-medium'>
          {messageState === 'failed' ? 'Failed to Copy!' : 'Copied!'}
        </p>
      </div>
    </>
  );
};
